import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import { useAuth } from "hooks/useAuth";
import { ClassAttributes, ComponentProps, HTMLAttributes } from "react";
import { getDday } from "utils/dday";

import { Text } from "../../core/Text";

const ItemValue = (
    props: Omit<ComponentProps<typeof Text>, "variant" | "color">
) => <Text variant="body3" color="text.primary" {...props} />;

export function ContentCTAButton({
    ctaText,
    disabledText,
    className,
    ...props
}: {
    ctaText: string;
    disabledText?: string;
} & ComponentProps<typeof Button>) {
    return (
        <Button
            className={cn("w-full tracking-normal", className)}
            size="large"
            {...props}
        >
            {props.disabled ? disabledText || ctaText : ctaText}
        </Button>
    );
}

export function ProductCTAButton({
    product,
    onPaymentBtnClick,
    onGuestPaymentBtnClick,
    ...props
}: {
    product: ProductFragment;
    onPaymentBtnClick: () => void;
    onGuestPaymentBtnClick?: () => void;
} & ComponentProps<typeof ContentCTAButton>) {
    const { me } = useAuth();

    return (
        <>
            <div
                className="flex items-center justify-between border-t
                    border-text-focus-visible py-4"
            >
                <ItemValue>가격</ItemValue>
                <Text
                    className="font-normal text-[#cd0000]"
                    variant="subtitle1"
                >
                    {`${(product.on_discount
                        ? product.discount_price
                        : product.price
                    ).toLocaleString()}원`}
                </Text>
            </div>
            <div className="flex flex-col gap-2">
                {product.on_discount && (
                    <Text
                        className="flex justify-center font-medium"
                        variant="caption1"
                        color="primary.main"
                    >
                        {`${
                            getDday(product.discount_end_time) > 0
                                ? `${getDday(
                                      product.discount_end_time
                                  )}일 이후에`
                                : "오늘"
                        } 할인이 종료됩니다`}
                    </Text>
                )}
                <ContentCTAButton onClick={onPaymentBtnClick} {...props} />
                {!me &&
                    product.on_order &&
                    product.is_guest_payment_allowed &&
                    onGuestPaymentBtnClick && (
                        <>
                            <ContentCTAButton
                                {...props}
                                ctaText={"비회원으로 " + props.ctaText}
                                variant="filled"
                                color="secondary"
                                onClick={onGuestPaymentBtnClick}
                            />
                            <GuestPaymentNotice />
                        </>
                    )}
            </div>
        </>
    );
}

export const GuestPaymentNotice = (
    props: ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>
) => {
    return (
        <div
            className={cn(
                "flex flex-col gap-1 rounded-lg bg-grey-100 px-3 py-2.5",
                props.className
            )}
            {...props}
        >
            <div
                className="text-[12px] font-semibold leading-[140%]
                    text-text-primary"
            >
                비회원 결제 시 유의사항
            </div>
            <p
                className="text-[10px] font-normal leading-[140%]
                    text-text-secondary"
            >
                {`비회원으로 결제하시면 믹스 홈페이지에서 구매내역을 조회하실 수 없습니다. 입력하신 이메일로 구매내역이 발송되므로, 이메일 주소를 정확히 입력해 주시기 바랍니다. 결제 취소는 믹스 홈페이지 우측 하단의 톡 버튼 또는 mix@team-mint.io로 문의해 주세요.`}
            </p>
        </div>
    );
};
